import React, { useState, useRef, useMemo } from 'react';
import { useT, T } from '@transifex/react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchLg } from 'src/ui-new/icons';
import { useDebounce } from 'use-debounce';
import { apiClient } from 'src/api/client';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type PropertyOption = {
  id: string;
  value: string;
  label: string;
  data: $TSFixMe;
};

export type AddressSelectProps = {
  shouldShowOptions?: boolean;
  onChange?: (value: string, option: PropertyOption) => void;
  className?: string;
};

/**
 * shadcn-based replacement for the legacy `SelectProperty`. Same API:
 * `onChange(value, option)` and `shouldShowOptions` for tenants that
 * surface all properties without a search term. Hits the same backend.
 */
export const AddressSelect: React.FC<AddressSelectProps> = ({
  onChange,
  shouldShowOptions,
  className,
}) => {
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const queryVariables = { search: debouncedSearch?.[0] };

  const { data: queryData } = useQuery({
    queryKey: ['SelectProperty', queryVariables.search],
    queryFn: () => apiClient.get('property/', { params: queryVariables }),
    placeholderData: keepPreviousData,
    // Address lookup is stable — typing back and forth reuses the cache.
    staleTime: 60_000,
    enabled: !!queryVariables.search || shouldShowOptions,
  });

  const options: PropertyOption[] = useMemo(() => {
    const raw = (queryData as $TSFixMe)?.data;
    if (!raw) return [];
    if (!queryVariables.search && !shouldShowOptions) return [];
    return raw.map((property: $TSFixMe) => ({
      id: property.id,
      value: property.id,
      label: property.location.name,
      data: property,
    }));
  }, [queryData, queryVariables.search, shouldShowOptions]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const select = (opt: PropertyOption) => {
    setSelectedLabel(opt.label);
    setSearch('');
    setOpen(false);
    onChange?.(opt.value, opt);
  };

  const showEmpty =
    open &&
    (queryVariables.search || shouldShowOptions) &&
    options.length === 0;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <SearchLg className="pointer-events-none absolute left-4 top-1/2 size-6 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          className="h-12 rounded-[var(--wh-radius-m,12px)] bg-background pl-[52px] pr-4 text-base shadow-[0_1px_0_var(--border-default)] transition-colors not-focus:hover:bg-muted md:text-base"
          placeholder={selectedLabel || t('Search address')}
          value={open ? search : selectedLabel}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setOpen(true);
            setSearch(e.target.value);
          }}
        />
      </div>

      {open && (showEmpty || options.length > 0) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[260px] overflow-auto rounded-xl border bg-popover p-1 text-popover-foreground shadow-[0_1px_0_var(--border-default)]">
          {showEmpty ? (
            <div className="flex h-10 items-center justify-center px-4 text-base text-muted-foreground">
              <T _str="No address found" />
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => select(opt)}
                className={cn(
                  'relative flex w-full items-center rounded-lg px-4 py-2 text-left text-base font-normal text-muted-foreground outline-hidden transition-colors hover:bg-muted',
                  selectedLabel === opt.label &&
                    'bg-muted font-medium text-foreground'
                )}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
